import { useEffect, useState } from 'react';
import i18nInst from '@shared/i18n';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { useBodyScrollLock } from '@shared/hooks/useBodyScrollLock';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import { useHandleVerification, type HandleVerif } from '@shared/hooks/useHandleVerification';
import PostViewerModal from '@shared/ui/PostViewerModal';
import { SignedImg } from '@shared/ui/SignedMedia';
import { IconX, IconHeart, IconChat } from '@shared/ui/icons';
import { currentLocale } from '@shared/i18n';

/** One row from public.get_advertiser_mentions. */
interface MentionRow {
  kind: 'post' | 'story';
  mention_id: string;
  brand_id: string | null;
  brand_name: string | null;
  partnership_type: string | null;
  detected_at: string | null;
  ig_id: string | null;
  instagram_provider_id: string | null;
  handle: string | null;
  caption: string | null;
  likes: number | null;
  comments: number | null;
  posted_at: string | null;
  thumb_url: string | null;
  permalink: string | null;
  is_paid_partnership: boolean | null;
  post_type?: string | null;
  child_posts?: unknown;
  video_url?: string | null;
  advertiser_name?: string | null;
}

interface Props {
  advertiserId: string;
  advertiserName: string;
  categoryId?: string | null;
  subcategoryId?: string | null;
  windowFrom: string;
  windowTo: string;
  onClose: () => void;
}

export default function AdvertiserMentionsModal({
  advertiserId,
  advertiserName,
  categoryId,
  subcategoryId,
  windowFrom,
  windowTo,
  onClose,
}: Props) {
  const { t } = useTranslation('customer');
  const [viewing, setViewing] = useState<MentionRow | null>(null);

  useCloseOnTabSwitch(onClose);
  useBodyScrollLock(); // lock body scroll while open (counted; leak-proof when stacked)
  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = useSupabaseQuery<MentionRow[]>(
    ['advertiser-mentions', advertiserId, windowFrom, windowTo, categoryId ?? '', subcategoryId ?? ''],
    async () =>
      supabase.rpc('get_advertiser_mentions', {
        p_advertiser_id: advertiserId,
        p_window_from: windowFrom,
        p_window_to: windowTo,
        p_category_id: categoryId ?? null,
        p_subcategory_id: subcategoryId ?? null,
        p_limit: 60,
      }),
  );

  const rows = q.data ?? [];
  const postCount = rows.filter((r) => r.kind === 'post').length;
  const storyCount = rows.filter((r) => r.kind === 'story').length;
  const verif = useHandleVerification(rows.map((r) => r.handle));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('social.analytics.mentions.title', {
        defaultValue: 'Mentions: {{name}}',
        name: advertiserName,
      })}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '4vh 16px',
        overflowY: 'auto',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 1100,
          background: 'var(--bg-elev, #161b22)',
          border: '1px solid var(--border-strong, #2a2d35)',
          borderRadius: 14,
          padding: 24,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        <SectionErrorBoundary name="advertiser-mentions">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--fs-caption)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'var(--muted)',
                }}
              >
                {t('social.analytics.mentions.kicker', { defaultValue: 'Brand mentions' })}
              </div>
              <h2 style={{ margin: '4px 0 0', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>
                {advertiserName}
              </h2>
              <div style={{ marginTop: 6, fontSize: 'var(--fs-body)', color: 'var(--muted)' }}>
                {q.isLoading
                  ? t('common.loading', { defaultValue: 'Loading…' })
                  : t('social.analytics.mentions.count', {
                      defaultValue: '{{posts}} posts · {{stories}} stories · window {{from}} → {{to}}',
                      posts: postCount,
                      stories: storyCount,
                      from: windowFrom,
                      to: windowTo,
                    })}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close', { defaultValue: 'Close' })}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'var(--text)',
                width: 32,
                height: 32,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 'var(--fs-label)',
                lineHeight: 1,
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconX size={16} />
            </button>
          </div>

          {q.isLoading ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 12,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} height={220} />
              ))}
            </div>
          ) : q.error ? (
            <div style={{ color: 'var(--danger)' }}>
              {String((q.error as Error)?.message ?? q.error)}
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{
                padding: 24,
                color: 'var(--muted)',
                textAlign: 'center',
                background: 'rgba(15,45,100,0.05)',
                border: '1px dashed rgba(15,45,100,0.3)',
                borderRadius: 10,
              }}
            >
              {t('social.analytics.mentions.empty', {
                defaultValue: 'No mentions found in this window.',
              })}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 12,
              }}
            >
              {rows.map((r) => (
                <MentionCard key={r.mention_id} r={r} onClick={() => setViewing(r)} advertiserName={advertiserName} verif={verif} />
              ))}
            </div>
          )}
        </SectionErrorBoundary>
      </div>

      {viewing && (
        <PostViewerModal
          post={{
            id: viewing.mention_id,
            post_type: viewing.post_type ?? viewing.kind,
            thumb_url: viewing.thumb_url,
            video_url: viewing.video_url ?? null,
            child_posts: viewing.child_posts,
            caption: viewing.caption,
            likes: viewing.likes,
            comments: viewing.comments,
            posted_at: viewing.posted_at,
            permalink: viewing.permalink,
            handle: viewing.handle,
            brand_name: viewing.brand_name,
            advertiser_name: advertiserName,
          }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function MentionCard({ r, onClick, advertiserName: _advertiserName, verif }: { r: MentionRow; onClick: () => void; advertiserName: string; verif: (h: string | null | undefined) => HandleVerif }) {
  const { t } = useTranslation('customer');
  const captionShort =
    r.caption && r.caption.length > 140 ? `${r.caption.slice(0, 140)}…` : r.caption ?? '';
  const dateLabel = r.posted_at ? new Date(r.posted_at).toLocaleDateString(currentLocale()) : '—';

  const card = (
    <div
      style={{
        background: 'rgba(15,45,100,0.10)',
        border: '1px solid rgba(15,45,100,0.4)',
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '1 / 1', background: 'rgba(0,0,0,0.4)' }}>
        {r.thumb_url ? (
          <SignedImg
            src={r.thumb_url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            loading="lazy"
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--dim)',
              fontSize: 'var(--fs-h1)',
            }}
          >
            ◯
          </div>
        )}
        {/* Top-left chips: kind + paid */}
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <span style={chipStyle(r.kind === 'post' ? '#4a9eff' : '#9B5DE5')}>
            {r.kind === 'post'
              ? t('social.analytics.mentions.chip.post', { defaultValue: 'POST' })
              : t('social.analytics.mentions.chip.story', { defaultValue: 'STORY' })}
          </span>
          {r.is_paid_partnership && (
            <span style={chipStyle('#f5b700')}>
              {t('social.analytics.mentions.chip.paid', { defaultValue: 'PAID' })}
            </span>
          )}
        </div>
      </div>
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {r.handle ? <>@{r.handle}<VerifiedBadge {...verif(r.handle)} size={15} /></> : '—'}
          </span>
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)' }}>{dateLabel}</span>
        </div>
        {captionShort && (
          <div
            style={{
              fontSize: 'var(--fs-small)',
              color: 'var(--text-2)',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {captionShort}
          </div>
        )}
        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            gap: 10,
            fontSize: 'var(--fs-caption)',
            color: 'var(--muted)',
            paddingTop: 4,
          }}
        >
          {r.kind === 'post' && (
            <>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><IconHeart size={11} color="#9aa" /> {(r.likes ?? 0).toLocaleString(i18nInst.language)}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><IconChat size={11} color="#9aa" /> {(r.comments ?? 0).toLocaleString(i18nInst.language)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: 'transparent', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', color: 'inherit', width: '100%' }}
      title={t('social.analytics.mentions.openPost', { defaultValue: 'View post' })}
    >
      {card}
    </button>
  );
}

function chipStyle(color: string): React.CSSProperties {
  return {
    fontSize: 'var(--fs-micro)',
    fontWeight: 'var(--fw-bold)',
    padding: '2px 6px',
    borderRadius: 4,
    background: 'rgba(0,0,0,0.55)',
    color,
    letterSpacing: 0.5,
    border: `1px solid ${color}`,
    backdropFilter: 'blur(4px)',
  };
}
